import type { Field } from '@/api/types/crm/crm.types'
import type { KanbanFilters } from '../store/kanban.types'

export type KanbanFieldFilterRange = {
  from?: string
  to?: string
}

export type KanbanFieldFilterValue =
  | string
  | string[]
  | number[]
  | boolean
  | KanbanFieldFilterRange

export const TRAFFIC_CHANNEL_FIELD_UNIQUE_NAME = 'TRAFFIC_CHANNEL'

export const isTrafficChannelKanbanField = (
  field: Pick<Field, 'unique_name'>,
): boolean => field.unique_name === TRAFFIC_CHANNEL_FIELD_UNIQUE_NAME

export const isKanbanFieldFilterValueEmpty = (
  value: KanbanFieldFilterValue | undefined | null,
): boolean => {
  if (value == null) return true
  if (typeof value === 'boolean') return false
  if (typeof value === 'string') return value.trim() === ''
  if (Array.isArray(value)) return value.length === 0
  const from = value.from?.trim() ?? ''
  const to = value.to?.trim() ?? ''
  return !from && !to
}

const normalizeKanbanFieldFilterEntry = (
  value: KanbanFieldFilterValue | null | undefined,
): KanbanFieldFilterValue | null => {
  if (value == null || isKanbanFieldFilterValueEmpty(value)) return null
  if (typeof value === 'boolean') return value
  if (typeof value === 'string') {
    return value.trim()
  }
  if (Array.isArray(value)) return value

  const from = value.from?.trim() ?? ''
  const to = value.to?.trim() ?? ''
  if (!from && !to) return null
  return {
    ...(from ? { from } : {}),
    ...(to ? { to } : {}),
  }
}

export const normalizeKanbanFieldFilters = (
  fieldFilters?: Record<string, KanbanFieldFilterValue> | null,
): Record<string, KanbanFieldFilterValue> | undefined => {
  if (!fieldFilters) return undefined
  const next: Record<string, KanbanFieldFilterValue> = {}
  for (const [key, value] of Object.entries(fieldFilters)) {
    const normalized = normalizeKanbanFieldFilterEntry(value)
    // `false` — валидное значение фильтра (boolean/file «Нет» / «Без файла»)
    if (normalized !== null && normalized !== undefined) {
      next[key] = normalized
    }
  }
  return Object.keys(next).length > 0 ? next : undefined
}

export const serializeKanbanFieldFilters = (
  fieldFilters?: Record<string, KanbanFieldFilterValue> | null,
): string => {
  const normalized = normalizeKanbanFieldFilters(fieldFilters) ?? {}
  const sortedKeys = Object.keys(normalized).sort((a, b) => Number(a) - Number(b))
  const sorted = sortedKeys.reduce<Record<string, KanbanFieldFilterValue>>(
    (acc, key) => {
      acc[key] = normalized[key]
      return acc
    },
    {},
  )
  return JSON.stringify(sorted)
}

export const pickKanbanUrlFilters = (filters: KanbanFilters) => ({
  search: filters.search ?? null,
  managerId: filters.managerId ?? null,
  nextContactDateFrom: filters.nextContactDateFrom ?? null,
  nextContactDateTo: filters.nextContactDateTo ?? null,
  lastActivityDateFrom: filters.lastActivityDateFrom ?? null,
  lastActivityDateTo: filters.lastActivityDateTo ?? null,
  source: filters.source ?? null,
  overdueOnly: filters.overdueOnly ?? false,
  tasksOverdueFrom: filters.tasksOverdueFrom ?? null,
  tasksTodayFrom: filters.tasksTodayFrom ?? null,
})

const appendFieldFilterValueToSearchParams = (
  query: URLSearchParams,
  rootKey: 'filters' | 'filter',
  fieldId: string,
  value: KanbanFieldFilterValue,
) => {
  if (isKanbanFieldFilterValueEmpty(value)) return

  if (typeof value === 'boolean') {
    query.set(`${rootKey}[field_filters][${fieldId}]`, value ? 'true' : 'false')
    return
  }

  if (typeof value === 'string') {
    const trimmed = value.trim()
    if (trimmed) {
      query.set(`${rootKey}[field_filters][${fieldId}]`, trimmed)
    }
    return
  }

  if (Array.isArray(value)) {
    value.forEach((item) => {
      if (item === undefined || item === null || item === '') return
      query.append(`${rootKey}[field_filters][${fieldId}][]`, String(item))
    })
    return
  }

  if (value.from) {
    query.set(`${rootKey}[field_filters][${fieldId}][from]`, String(value.from))
  }
  if (value.to) {
    query.set(`${rootKey}[field_filters][${fieldId}][to]`, String(value.to))
  }
}

export const appendKanbanFieldFiltersToSearchParams = (
  query: URLSearchParams,
  fieldFilters: Record<string, KanbanFieldFilterValue>,
) => {
  Object.entries(fieldFilters).forEach(([fieldId, value]) => {
    appendFieldFilterValueToSearchParams(query, 'filters', fieldId, value)
  })
}

/** Элементы для `getParams`: `filter` + `[field_filters][12][]=hot`. */
export const buildLeadFieldFilterParamEntries = (
  fieldFilters?: Record<string, KanbanFieldFilterValue> | null,
): string[] => {
  const normalized = normalizeKanbanFieldFilters(fieldFilters)
  if (!normalized) return []

  const query = new URLSearchParams()
  Object.entries(normalized).forEach(([fieldId, value]) => {
    appendFieldFilterValueToSearchParams(query, 'filter', fieldId, value)
  })

  const entries: string[] = []
  query.forEach((value, key) => {
    // key = filter[field_filters][12][] → нужен хвост [field_filters][12][]=value
    if (!key.startsWith('filter')) return
    entries.push(`${key.slice('filter'.length)}=${value}`)
  })
  return entries
}
